import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as BlogService from 'services/agents/content/blog';

export const useBlogService = () => {
  const { token } = useSelector((store) => store.user_data);

  const createBlog = async (data, options = {}) => {
    const { error, payload: blog } = await BlogService.create({
      ...options,
      data,
      token
    });

    if (error) {
      toast.error('Cannot create blog at this time');
      return { blog: {} };
    }

    toast.success('Blog created successfully.');
    return { blog };
  };

  const deleteBlog = async (id, options = {}) => {
    const { error } = await BlogService.deleteOne(id, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to delete blog at this time.');
      return false;
    }

    toast.success('Blog deleted successfully');
    return true;
  };

  const deleteBlogs = async (options = {}) => {
    const { error } = await BlogService.deleteMany({
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to delete blogs at this time.');
      return false;
    }

    toast.success('Blogs deleted successfully');
    return true;
  };

  const fetchBlog = async (id, options = {}) => {
    const { error, payload: blog } = await BlogService.readById(id, { ...options, token });

    if (error) {
      toast.error(error);
      return { blog: null };
    }

    return { blog };
  };

  const fetchBlogs = async (options = {}) => {
    const { error, payload } = await BlogService.read({ ...options, token });

    if (error) {
      toast.error(error);
      return { blogs: [], size: 0 };
    }

    const {
      data: blogs,
      meta: { size }
    } = payload;
    return { blogs, size };
  };

  const fetchBlogAnalytics = async (options = {}) => {
    const { error, payload } = await BlogService.fetchAnalytics({ ...options, token });

    if (error) {
      toast.error(error);
      return { analytics: null };
    }

    return { analytics: payload };
  };

  const fetchBlogRuns = async (options = {}) => {
    const { error, payload } = await BlogService.fetchBlogRuns({ ...options, token });

    if (error) {
      toast.error(error);
      return { blog_runs: [], size: 0 };
    }

    const {
      data: blog_runs,
      meta: { size }
    } = payload;
    return { blog_runs, size };
  };

  const fetchBlogRun = async (id, options = {}) => {
    const { error, payload: blog_run } = await BlogService.fetchBlogRunById(id, {
      ...options,
      token
    });

    if (error) {
      toast.error(error);
      return { blog_run: null };
    }

    return { blog_run };
  };

  const triggerBlogRun = async (options = {}) => {
    const { error } = await BlogService.triggerBlogRun({ ...options, token });

    if (error) {
      toast.error(error || 'Unable to trigger blog run at this time.');
      return false;
    }

    toast.success('Blog generation started');
    return true;
  };

  const manageBlog = async (id, action, options = {}) => {
    const { error } = await BlogService.manage(id, action, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to perform action on blog at this time.');
      return false;
    }

    toast.success('Blog updated successfully');
    return true;
  };

  const acceptBlog = async (id, options = {}) => {
    const { error } = await BlogService.accept(id, {
      ...options,
      token
    });

    if (error) {
      toast.error(error || 'Unable to accept blog at this time.');
      return false;
    }

    toast.success('Blog accepted successfully');
    return true;
  };

  const rejectBlog = async (id, reason, options = {}) => {
    const payload = {
      reason,
      rejection_reason: reason
    };
    const { error } = await BlogService.reject(id, {
      ...options,
      token,
      data: {
        ...payload,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to reject blog at this time.');
      return false;
    }

    toast.success('Blog rejected successfully');
    return true;
  };

  const regenerateBlogImage = async (id, options = {}) => {
    const { error } = await BlogService.regenerateImage(id, {
      ...options,
      token
    });

    if (error) {
      toast.error(error || 'Unable to regenerate image at this time.');
      return false;
    }

    toast.success('Image regeneration started');
    return true;
  };

  const searchBlogs = async (keys, keyword, options = {}) => {
    const { error, payload } = await BlogService.search(keys, keyword, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to fetch blogs at this time.');
      return { blogs: [], size: 0 };
    }

    const {
      data: blogs,
      meta: { size }
    } = payload;
    return { blogs, size };
  };

  const updateBlog = async (id, options = {}) => {
    const { error } = await BlogService.updateById(id, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to update blog at this time.');
      return false;
    }

    toast.success('Blog updated successfully');
    return true;
  };

  const updateBlogDraft = async (id, data = {}, options = {}) => {
    const { error, payload: blog } = await BlogService.updateDraft(id, {
      ...options,
      token,
      data: {
        ...data,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to update draft at this time.');
      return { success: false, blog: null };
    }

    toast.success('Draft updated successfully');
    return { success: true, blog: blog || null };
  };

  const updateBlogs = async (options = {}) => {
    const { error } = await BlogService.update({
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to update blogs at this time.');
      return false;
    }

    toast.success('Blogs updated successfully');
    return true;
  };

  return {
    createBlog,
    deleteBlog,
    deleteBlogs,
    fetchBlog,
    fetchBlogAnalytics,
    fetchBlogRun,
    fetchBlogRuns,
    fetchBlogs,
    acceptBlog,
    manageBlog,
    regenerateBlogImage,
    rejectBlog,
    searchBlogs,
    triggerBlogRun,
    updateBlogDraft,
    updateBlog,
    updateBlogs
  };
};
